import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { apiService } from '../../services/api';
import { ErdSchemaStateService } from '../../services/erd-schema-state-resolver';
import { transformSchemaToERD } from '../../utils/erd.utils';
import { KnowledgeGraphSchemaResponse, KnowledgeGraphSummaryResponse } from '../../types/api-response';
import { D3ERDProps, ERDMode, ErdViewModalProps, Position } from '../../types/erd';
import { D3EntityRelationDiagram } from './D3EntityRelationDiagram';
import '../shared.css';
import './ErdViewModal.css';

interface ApiErrorObject {
  status?: string;
  code?: string;
  title?: string;
  detail?: string;
}

interface ApiErrorResponse {
  errors?: ApiErrorObject[];
}

const erdSchemaStateService = new ErdSchemaStateService();

export const ErdViewModal: React.FC<ErdViewModalProps> = ({ kg_id, version_id, on_close }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentMode, setCurrentMode] = useState<ERDMode>('view');
  const [currentNodesPos, setCurrentNodesPos] = useState<Record<string, Position>>({});
  const [graphData, setGraphData] = useState<D3ERDProps>({ nodes: [], edges: [] });

  useEffect((): void => {
    void loadSchemaAndSummary();
  }, [kg_id, version_id]);

  const loadSchemaAndSummary = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const [schemaResult, summaryResult]: [
        PromiseSettledResult<KnowledgeGraphSchemaResponse>,
        PromiseSettledResult<KnowledgeGraphSummaryResponse>,
      ] = await Promise.allSettled([
        apiService.getERDSchema({ kg_id, version_id }),
        apiService.getERDSummary({ kg_id, version_id }),
      ]);

      if (
        schemaResult.status !== 'fulfilled' ||
        summaryResult.status !== 'fulfilled'
      ) {
        return;
      }

      const schema = schemaResult.value;
      const summary = summaryResult.value;
      const resolvedState = await erdSchemaStateService.getResolvedSchemaState(kg_id, version_id,);

      if (resolvedState.sourceVersionId) {
        alert(`Using ERD layout from version ${resolvedState.sourceVersionId}. Please review and save for current version.`);
      }

      let result: D3ERDProps = transformSchemaToERD(schema.data.attributes, summary.data.attributes.summary);

      if (Object.keys(resolvedState.positions).length > 0) {
        setCurrentNodesPos(resolvedState.positions);
        result = {
          ...result,
          nodes: result.nodes.map((node) => {
            const saved = resolvedState.positions[node.id];

            if (!saved) {
              return node;
            }

            return {
              ...node,
              x: saved.x,
              y: saved.y,
              fx: saved.x,
              fy: saved.y,
            };
          }),
        };
      }

      setGraphData(result);
    } catch (error: unknown) {
      console.error('Failed to load schema and summary', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleERDViewState = async (): Promise<void> => {
    if (!Object.keys(currentNodesPos).length) {
      setCurrentMode('view');
      return;
    }

    try {
      const response = await apiService.createErdSchemaStateUrl(kg_id, version_id);

      if (
        response &&
        response.data &&
        response.data.attributes &&
        response.data.attributes.upload_url
      ) {
        const uploadUrl = response.data.attributes.upload_url;

        await apiService.updateErdSchemaState(uploadUrl, currentNodesPos);
      }
    } catch (err: unknown) {
      const defaultErrorMessage = 'Failed to create KG Schema ERD State. Please try again.';

      if (!(err instanceof AxiosError)) {
        alert(defaultErrorMessage);
        console.error('Error', err);
        return;
      }

      const responseData = err.response
        ? (err.response.data as ApiErrorResponse)
        : undefined;

      let firstError:
        | ApiErrorObject
        | undefined;

      if (
        responseData &&
        responseData.errors &&
        responseData.errors.length > 0
      ) {
        firstError = responseData.errors[0];
      }

      if (
        firstError &&
        firstError.status === '403'
      ) {
        alert('You do not have permission to create KG Schema ERD State.');
      } else {
        alert(defaultErrorMessage);
      }

      console.error('Error', err);
      return;
    }

    setGraphData((prev) => ({
      ...prev,
      nodes: prev.nodes.map((node) => {
        const position =
          currentNodesPos[node.id];

        if (!position) {
          return node;
        }

        return {
          ...node,
          x: position.x,
          y: position.y,
          fx: position.x,
          fy: position.y,
        };
      }),
    }));

    setCurrentMode('view');
  };

  const handleERDMode = (): void => {
    if (currentMode === 'view') {
      setCurrentMode('edit');
      return;
    }

    setCurrentNodesPos({});
    void loadSchemaAndSummary();
    setCurrentMode('view');
  };

  return (
    <div
      className="modal-overlay"
      onClick={on_close}
    >
      <div
        className="modal-content erd-view-modal"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <div className="modal-header">
          <h2>ERD Initial View Editor</h2>

          <div className="header-buttons">
            <button
              className={`tab-btn ${currentMode === 'edit' ? 'tab-btn--danger' : 'tab-btn--ghost'}`}
              disabled={isLoading}
              onClick={handleERDMode}
            >
              {currentMode === 'view' ? 'Edit' : 'Reset'}
            </button>

            <button
              className="tab-btn tab-btn--primary"
              disabled={isLoading || currentMode === 'view'}
              onClick={handleERDViewState}
            >
              Save
            </button>

            <button
              className="modal-close"
              onClick={on_close}
            >
              ×
            </button>
          </div>
        </div>

        <div className="erd-modal-body">
          {isLoading ? (
            <div className="loading-overlay">
              <div className="loader"></div>
            </div>
          ) : (
            <div className="erd-graph-container">
              <D3EntityRelationDiagram
                mode={currentMode}
                nodes={graphData.nodes}
                edges={graphData.edges}
                onNodesChange={(nodes) => {
                  const map: Record<
                    string,
                    Position
                  > = {};

                  nodes.forEach((node) => {
                    if (
                      node.x !== undefined &&
                      node.y !== undefined
                    ) {
                      map[node.id] = {
                        x: node.x,
                        y: node.y,
                      };
                    }
                  });

                  setCurrentNodesPos(map);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};